import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  Form, 
  Upload, 
  message, 
  Spin, 
  Row, 
  Col, 
  Checkbox, 
  InputNumber,
  Alert,
  Space,
  Tag,
  Modal,
  Table,
  Select,
  Tooltip,
  Tabs
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  ClusterOutlined, 
  ArrowLeftOutlined, 
  InboxOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  StopOutlined,
  EyeOutlined
} from '@ant-design/icons';
import MolStarViewer from '../../../components/results/protein/structure/MolStarViewer';
import ProteinMPNNResults from '../../../components/results/protein/ProteinMPNNResults';
import session from '../../../utils/session';
import { createTask, getTasksWithCurrent, cancelTask, TaskResponse } from '../../../utils/taskManager';
import { shouldDisplayInList } from '../../../utils/taskManager';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

interface ChainInfo {
  chainId: string;
  residueCount: number;
}

interface StructureInfo {
  chains: string[];
  chainDetails?: ChainInfo[];
  filename?: string;
  sequences?: { [chainId: string]: string };
  residueNumbers?: { [chainId: string]: number[] };  // 序列索引 -> PDB残基编号映射
}

interface FixedPosition {
  [chainId: string]: number[];
}

interface FixedPositions {
  [filename: string]: FixedPosition;
}

interface SelectedResidue {
  chainId: string;
  residueNumber: number;
  residueName: string;
}

interface MolStarViewerProps {
  pdbData: string;
  title?: string;
  height?: number;
  selectedResidues?: {[chainId: string]: number[]};
  onResidueClick?: (chainId: string, residueNumber: number) => void;
}

type ProteinMPNNTaskResult = {
  sequences?: Array<{
    sample: number;
    score: number;
    sequence: string;
    header: string;
  }>;
  results?: Array<{
    sequence_id: string;
    result: {
      sample: number;
      score: number;
      sequence: string;
      header: string;
      fasta_contents?: Array<{
        filename: string;
        content: string;
      }>;
    };
    success: boolean;
    error: string | null;
  }>;
  total_sequences?: number;
  successful_count?: number;
  failed_count?: number;
  artifacts?: any;
  meta?: any;
  runtime?: number;
};

export default function ProteinMPNNPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // 状态管理
  const [file, setFile] = useState<File | null>(null);
  const [pdbData, setPdbData] = useState<string | null>(null);
  const [structureInfo, setStructureInfo] = useState<StructureInfo | null>(null);
  const [selectedChains, setSelectedChains] = useState<string[]>([]);
  const [fixedPositions, setFixedPositions] = useState<FixedPositions>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [currentTaskResult, setCurrentTaskResult] = useState<ProteinMPNNTaskResult | null>(null);
  const [selectedSequenceIndex, setSelectedSequenceIndex] = useState<number>(0);
  const [viewerVisible, setViewerVisible] = useState<boolean>(false);
  const [selectedSequence, setSelectedSequence] = useState<string | null>(null);
  const [selectedResidues, setSelectedResidues] = useState<{[chainId: string]: number[]}>({});
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [pendingTaskParams, setPendingTaskParams] = useState<any>(null);

  const sessionId = useMemo(() => session.getSessionId(), []);

  // 组件挂载时自动刷新任务列表
  useEffect(() => {
    refreshTasks();
    const interval = setInterval(refreshTasks, 10000);
    return () => clearInterval(interval);
  }, []);

  // 处理文件上传
  const handleFileChange = (info: any) => {
    if (info.file.status === 'removed') { 
      setFile(null); 
      setPdbData(null);
      setStructureInfo(null);
      setSelectedChains([]);
      setSelectedResidues({});
      setFixedPositions({});
      return; 
    }
    const fileObj = info.file.originFileObj || (info.file.status !== 'uploading' ? info.file : null);
    if (fileObj instanceof File) {
      setFile(fileObj);
      
      const loadingMessage = message.loading({ content: '正在解析PDB结构文件...', duration: 0 });
      
      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          const pdbContent = event.target.result.toString();
          setPdbData(pdbContent);
          
          // 从PDB文件内容中识别实际的链和序列
          const chains = extractChainsFromPDB(pdbContent);
          const { sequences, residueNumbers } = extractSequencesFromPDB(pdbContent);
          const info: StructureInfo = {
            chains: chains,
            sequences: sequences,
            residueNumbers: residueNumbers,
            filename: fileObj.name.replace(/\.[^/.]+$/, "")
          };
          setStructureInfo(info);
          setSelectedChains(chains);
          
          // 初始化固定位置和选中残基
          const initialFixed: FixedPosition = {};
          const initialSelected: {[chainId: string]: number[]} = {};
          chains.forEach(chain => {
            initialFixed[chain] = [];
            initialSelected[chain] = [];
          });
          setFixedPositions({ [info.filename!]: initialFixed });
          setSelectedResidues(initialSelected);
          
          loadingMessage();
          message.success(`识别到${chains.length}个链: ${chains.join(', ')}`);
        }
      };
      reader.onerror = () => {
        loadingMessage();
        message.error('读取PDB文件失败');
      };
      reader.readAsText(fileObj);
    }
  };

  // 处理链选择变更
  const handleChainSelectionChange = (checkedValues: string[]) => {
    setSelectedChains(checkedValues);
    form.setFieldsValue({ selected_chains: checkedValues });
  };

  // 刷新任务列表
  const refreshTasks = async () => {
    try {
      const list = await getTasksWithCurrent(0, 50, undefined, 'structure_design', 'proteinmpnn');
      console.log('获取到的任务列表:', list);
      
      // 过滤掉已删除的任务
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const filteredList = list.filter(task => !deletedTaskIds.includes(task.id));
      
      // 调试：检查过滤前后的任务数量
      console.log('删除过滤后的任务数量:', filteredList.length);
      
      // 接入 shouldDisplayInList，仅展示应显示在列表中的长时任务
      const displayList = filteredList.filter(shouldDisplayInList);
      console.log('shouldDisplayInList过滤后的任务数量:', displayList.length);
      
      // 如果过滤后为空，直接显示所有未删除的任务
      if (displayList.length === 0 && filteredList.length > 0) {
        console.log('shouldDisplayInList过滤导致任务为空，显示所有任务');
        setTasks(filteredList);
      } else {
        setTasks(displayList);
      }
    } catch (e) {
      console.error('获取任务列表失败:', e);
    }
  };

  // 处理残基选择
  const handleResidueSelection = (chainId: string, position: number, isSelected: boolean) => {
    setSelectedResidues(prev => {
      const newSelected = { ...prev };
      if (!newSelected[chainId]) {
        newSelected[chainId] = [];
      }
      
      if (isSelected) {
        if (!newSelected[chainId].includes(position)) {
          newSelected[chainId] = [...newSelected[chainId], position].sort((a, b) => a - b);
        }
      } else {
        newSelected[chainId] = newSelected[chainId].filter(pos => pos !== position);
      }
      
      return newSelected;
    });
    
    // 更新固定位置
    const filename = structureInfo?.filename || 'structure';
    setFixedPositions(prev => {
      const newFixed = { ...prev };
      if (!newFixed[filename]) {
        newFixed[filename] = {};
      }
      if (!newFixed[filename][chainId]) {
        newFixed[filename][chainId] = [];
      }
      
      if (isSelected) {
        if (!newFixed[filename][chainId].includes(position)) {
          newFixed[filename][chainId] = [...newFixed[filename][chainId], position].sort((a, b) => a - b);
        }
      } else {
        newFixed[filename][chainId] = newFixed[filename][chainId].filter(pos => pos !== position);
      }
      
      return newFixed;
    });
  };

  // 渲染可选择的序列
  const renderSelectableSequence = (chainId: string, sequence: string) => {
    const selectedPositions = selectedResidues[chainId] || [];
    const residueNums = structureInfo?.residueNumbers?.[chainId] || [];
    
    return (
      <div style={{ 
        fontFamily: 'monospace', 
        fontSize: '14px',
        lineHeight: '1.8',
        userSelect: 'none'
      }}>
        {sequence.split('').map((aa, index) => {
          // 使用实际的PDB残基编号，而不是序列索引
          const actualResNum = residueNums[index] || (index + 1);
          const isSelected = selectedPositions.includes(actualResNum);
          
          return (
            <span
              key={index}
              onClick={() => handleResidueSelection(chainId, actualResNum, !isSelected)}
              style={{
                backgroundColor: isSelected ? '#1890ff' : 'transparent',
                color: isSelected ? 'white' : '#000',
                padding: '2px 1px',
                cursor: 'pointer',
                borderRadius: '2px',
                margin: '0 1px'
              }}
              title={`残基 ${actualResNum}: ${aa}${isSelected ? ' (固定)' : ''}`}
            >
              {aa}
            </span>
          );
        })}
      </div>
    );
  };

  // 准备提交任务
  const prepareSubmitTask = async () => {
    if (!file) { 
      message.error('请上传PDB文件'); 
      return; 
    }
    if (selectedChains.length === 0) {
      message.error('请选择至少一个链');
      return;
    }

    try {
      const values = await form.validateFields();
      const filename = structureInfo?.filename || 'structure';
      const currentFixedPositions = filename && fixedPositions[filename] ? fixedPositions[filename] : {};
      
      // 准备任务参数
      const taskParams = {
        selected_chains: selectedChains,
        fixed_positions: currentFixedPositions,
        num_seq_per_target: values.num_seq_per_target || 10,
        sampling_temp: values.sampling_temp || 0.1
      };
      
      setPendingTaskParams(taskParams);
      setConfirmDialogVisible(true);
    } catch (e: any) {
      message.error('请检查参数设置');
    }
  };

  // 确认提交任务
  const confirmSubmitTask = async () => {
    if (!pendingTaskParams) return;
    
    try {
      setSubmitting(true);
      setError(null);
      setConfirmDialogVisible(false);

      // 将文件读取为文本内容
      const toText = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const pdbContent = await toText(file!);
      const filename = structureInfo?.filename || 'structure';

      const task = await createTask({
        name: 'ProteinMPNN 序列设计',
        description: 'ProteinMPNN protein sequence design',
        service_name: 'structure_design',
        task_type: 'proteinmpnn',
        handler_name: 'proteinmpnn_design_json',
        parameters: {
          pdb_content: pdbContent,
          pdb_filename: filename + '.pdb',
          selected_chains: pendingTaskParams.selected_chains,
          fixed_positions: pendingTaskParams.fixed_positions,
          num_seq_per_target: pendingTaskParams.num_seq_per_target,
          sampling_temp: pendingTaskParams.sampling_temp
        },
        priority: 5,
      });

      message.success(`任务已提交：${task.id}`);
      await refreshTasks();
      setPendingTaskParams(null);
    } catch (e: any) {
      setError(e?.message || '提交任务失败');
      message.error(`提交任务失败: ${e?.message || '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 任务表格列定义
  const columns = [
    { 
      title: '选择', 
      key: 'selection', 
      width: 60,
      render: (_: any, rec: TaskResponse) => (
        <Checkbox 
          checked={selectedTaskIds.includes(rec.id)}
          onChange={(e) => handleSelectTask(rec.id, e.target.checked)}
          disabled={rec.status === 'running' || rec.status === 'pending'}
        />
      )
    },
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 280, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s: string) => <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag> },
    { title: '进度', dataIndex: 'progress', key: 'progress', width: 100 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 200 },
    { title: '操作', key: 'action', render: (_: any, rec: TaskResponse) => {
      // ProteinMPNN结果检测逻辑 - 检查多种可能的结果格式
      const hasResult = rec.status === 'completed' && rec.result && (
        // 检查直接的sequences数组
        (rec.result.sequences && Array.isArray(rec.result.sequences) && rec.result.sequences.length > 0) ||
        // 检查统一格式中的results数组
        (rec.result.results && Array.isArray(rec.result.results) && rec.result.results.length > 0) ||
        // 检查是否有任何结果数据
        (rec.result.success === true && rec.result.sequences) ||
        // 检查artifacts中的结果
        (rec.result.artifacts && rec.result.artifacts.sample_contents && Object.keys(rec.result.artifacts.sample_contents).length > 0)
      );
      const canDelete = rec.status !== 'running' && rec.status !== 'pending';
      const canCancel = rec.status === 'running' || rec.status === 'pending' || rec.status === 'queued';
      
      console.log(`任务 ${rec.id} 结果状态:`, { 
        hasResult, 
        result: rec.result, 
        sequences: rec.result?.sequences,
        results: rec.result?.results,
        success: rec.result?.success,
        artifacts: rec.result?.artifacts,
        status: rec.status 
      });
      
      return (
        <Space>
          <Button size="small" onClick={() => showResults(rec)} disabled={!hasResult}>查看结果</Button>
          {canCancel && (
            <Button 
              size="small" 
              type="default"
              style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
              icon={<StopOutlined />} 
              onClick={() => handleCancelTask(rec.id)}
            >
              取消任务
            </Button>
          )}
          {canDelete && (
            <Button 
              size="small" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteTask(rec.id)}
            >
              删除
            </Button>
          )}
        </Space>
      );
    }},
  ];

  // 显示结果
  const showResults = (task: TaskResponse) => {
    console.log('查看任务结果:', task);
    console.log('任务结果详细信息:', JSON.stringify(task.result, null, 2));
    const res: ProteinMPNNTaskResult | undefined = (task.result as any) as ProteinMPNNTaskResult;
    
    setCurrentTaskResult(res || null);
    
    // 获取原始FASTA内容
    const fastaContent = getFastaContent(res);
    console.log('提取的FASTA内容:', fastaContent);
    if (fastaContent) {
      setSelectedSequence(fastaContent);
      setViewerVisible(true);
    } else {
      message.info('暂无序列结果');
    }
  };

  // 获取原始FASTA内容
  const getFastaContent = (res: ProteinMPNNTaskResult | null | undefined): string | null => {
    if (!res) {
      console.log('getFastaContent: res为空');
      return null;
    }
    
    console.log('getFastaContent: 检查结果结构:', res);
    
    // 检查新的简化格式：fasta_contents
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      console.log('getFastaContent: 找到results数组，长度:', res.results.length);
      const fastaContents = res.results[0]?.result?.fasta_contents;
      console.log('getFastaContent: fasta_contents:', fastaContents);
      if (fastaContents && Array.isArray(fastaContents) && fastaContents.length > 0) {
        console.log('getFastaContent: 找到fasta_contents，返回第一个文件内容');
        // 返回第一个FASTA文件的内容
        return fastaContents[0]?.content || null;
      }
    }
    
    console.log('getFastaContent: 未找到fasta_contents，返回null');
    return null;
  };

  // 检查是否有FASTA结果 - 保留用于按钮状态判断
  const hasFastaResults = (res: ProteinMPNNTaskResult | null | undefined): boolean => {
    if (!res) return false;
    
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const fastaContents = res.results[0]?.result?.fasta_contents;
      return Boolean(fastaContents && Array.isArray(fastaContents) && fastaContents.length > 0);
    }
    
    return false;
  };

  // 从PDB文件内容中提取链信息和序列
  const extractChainsFromPDB = (pdbContent: string): string[] => {
    const chains = new Set<string>();
    const lines = pdbContent.split('\n');
    
    for (const line of lines) {
      // 检查ATOM和HETATM记录
      if (line.startsWith('ATOM  ') || line.startsWith('HETATM')) {
        // PDB格式中链ID在第22个字符位置（从1开始计数，所以索引是21）
        const chainId = line.charAt(21);
        if (chainId && chainId.trim() !== '') {
          chains.add(chainId.trim());
        }
      }
    }
    
    const chainArray = Array.from(chains).sort();
    console.log('从PDB文件中识别到的链:', chainArray);
    return chainArray.length > 0 ? chainArray : ['A']; // 如果没有找到链，默认返回A
  };

  // 从PDB文件中提取序列信息和残基编号映射
  const extractSequencesFromPDB = (pdbContent: string): { 
    sequences: { [chainId: string]: string };
    residueNumbers: { [chainId: string]: number[] };
  } => {
    const sequences: { [chainId: string]: string } = {};
    const residueNumbers: { [chainId: string]: number[] } = {};
    const lines = pdbContent.split('\n');
    const chainResidues: { [chainId: string]: { [resNum: string]: string } } = {};
    
    // 三字母氨基酸代码到单字母代码的映射
    const aaMap: { [key: string]: string } = {
      'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
      'GLU': 'E', 'GLN': 'Q', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
      'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
      'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V'
    };
    
    for (const line of lines) {
      if (line.startsWith('ATOM  ')) {
        const chainId = line.charAt(21).trim();
        const resName = line.substring(17, 20).trim();
        const resNum = line.substring(22, 26).trim();
        const atomName = line.substring(12, 16).trim();
        
        // 只处理CA原子（每个残基的代表原子）
        if (atomName === 'CA' && aaMap[resName]) {
          if (!chainResidues[chainId]) {
            chainResidues[chainId] = {};
          }
          chainResidues[chainId][resNum] = aaMap[resName];
        }
      }
    }
    
    // 将残基按顺序组合成序列，同时保存残基编号映射
    for (const chainId in chainResidues) {
      const residues = chainResidues[chainId];
      const sortedResNums = Object.keys(residues).sort((a, b) => parseInt(a) - parseInt(b));
      sequences[chainId] = sortedResNums.map(resNum => residues[resNum]).join('');
      residueNumbers[chainId] = sortedResNums.map(resNum => parseInt(resNum));  // 保存实际的PDB残基编号
    }
    
    console.log('从PDB文件中提取的序列:', sequences);
    console.log('残基编号映射:', residueNumbers);
    return { sequences, residueNumbers };
  };

  // 注意：getAllFastaContents 函数已移至 ProteinMPNNResults 组件中，避免重复

  // 渲染序列结果 - 使用统一的 ProteinMPNNResults 组件
  const renderSequenceResults = () => {
    return <ProteinMPNNResults taskResult={currentTaskResult} />;
  };

  // 任务选择处理
  const handleSelectTask = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds(prev => [...prev, taskId]);
    } else {
      setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
    }
  };

  // 删除任务
  const handleDeleteTask = async (taskId: string) => {
    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      message.success('任务已从本地删除');
    } catch (error) {
      console.error('本地删除任务失败:', error);
      message.error('本地删除任务失败');
    }
  };

  // 批量删除任务
  const handleBatchDelete = async () => {
    if (selectedTaskIds.length === 0) {
      message.warning('请先选择要删除的任务');
      return;
    }

    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const newDeletedTasks = [...deletedTasks, ...selectedTaskIds];
      localStorage.setItem('deletedTaskIds', JSON.stringify(newDeletedTasks));
      
      setTasks(prevTasks => prevTasks.filter(task => !selectedTaskIds.includes(task.id)));
      setSelectedTaskIds([]);
      
      message.success(`已批量删除 ${selectedTaskIds.length} 个任务`);
    } catch (error) {
      console.error('批量删除任务失败:', error);
      message.error('批量删除任务失败');
    }
  };

  // 取消任务
  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务取消成功');
      await refreshTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  const uploadProps = { 
    name: 'file', 
    multiple: false, 
    maxCount: 1, 
    accept: '.pdb', 
    beforeUpload: () => false, 
    onChange: handleFileChange 
  } as const;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/structure')} style={{ marginBottom: 16 }}>
          返回结构与设计
        </Button>
        <Typography>
          <Title level={2}>ProteinMPNN 序列设计</Title>
          <Paragraph>
            通过任务管理系统提交ProteinMPNN序列设计任务，支持异步处理，实时查看任务进度与结果。
          </Paragraph>
        </Typography>
      </div>

      {/* 顶部：PDB上传 */}
      <Card title="PDB上传" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Form.Item name="pdb_file" label={<span>PDB结构文件<Tooltip title="上传蛋白质结构的PDB格式文件"><InfoCircleOutlined style={{ marginLeft: 8 }} /></Tooltip></span>} rules={[{ required: true, message: '请上传PDB文件' }]}>
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Card>

      <Row gutter={[16, 16]}>
        {/* 左侧：结构预览 */}
        <Col xs={24} lg={12}>
          {pdbData ? (
            <Card title="结构预览" style={{ height: 500 }}>
              <MolStarViewer
                pdbData={pdbData}
                height={400}
                title="蛋白质结构"
                selectedResidues={selectedResidues}
                onResidueClick={(chainId, residueNumber) => {
                  const isSelected = selectedResidues[chainId]?.includes(residueNumber) || false;
                  handleResidueSelection(chainId, residueNumber, !isSelected);
                }}
              />
            </Card>
          ) : (
            <Card title="结构预览" style={{ height: 500 }}>
              <div style={{ 
                height: 400, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '2px dashed #d9d9d9',
                borderRadius: 6
              }}>
                <Text type="secondary">请先上传PDB文件</Text>
              </div>
            </Card>
          )}
        </Col>

        {/* 右侧：序列和参数 */}
        <Col xs={24} lg={12}>
          {/* 序列显示 */}
          <Card title="序列" style={{ marginBottom: 16, height: 240 }}>
            {structureInfo && structureInfo.chains.length > 0 ? (
              <div style={{ height: 180 }}>
                {structureInfo.sequences && Object.keys(structureInfo.sequences).length > 0 ? (
                  <Tabs 
                    size="small"
                    style={{ height: '100%' }}
                    items={structureInfo.chains.map(chain => ({
                      key: chain,
                      label: `链 ${chain} (${structureInfo.sequences?.[chain]?.length || 0}残基)`,
                      children: (
                        <div style={{ 
                          background: '#f5f5f5', 
                          padding: 12, 
                          borderRadius: 4, 
                          height: 120,
                          overflow: 'auto'
                        }}>
                          {structureInfo.sequences?.[chain] ? (
                            <div>
                              <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
                                点击残基选择固定位置（蓝色为已选中）
                              </div>
                              {renderSelectableSequence(chain, structureInfo.sequences[chain]!)}
                              {selectedResidues[chain] && selectedResidues[chain].length > 0 && (
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#1890ff' }}>
                                  已选中 {selectedResidues[chain].length} 个固定位置: {selectedResidues[chain].join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <Text type="secondary">未检测到序列</Text>
                          )}
                        </div>
                      )
                    }))}
                  />
                ) : (
                  <div style={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: '#f5f5f5',
                    border: '1px dashed #d9d9d9',
                    borderRadius: 4
                  }}>
                    <Text type="secondary">未能从PDB文件中提取到序列信息</Text>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ 
                height: 180, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '1px dashed #d9d9d9',
                borderRadius: 4
              }}>
                <Text type="secondary">请先上传PDB文件以显示序列信息</Text>
              </div>
            )}
          </Card>

          {/* 参数设置 */}
          <Card title="参数设置" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical">
              {structureInfo && (
                <Form.Item label={`选择设计链 (共${structureInfo.chains.length}个链)`} style={{ marginBottom: 12 }}>
                  <Checkbox.Group
                    options={structureInfo.chains.map(chain => ({
                      label: `链 ${chain}`,
                      value: chain
                    }))}
                    value={selectedChains}
                    onChange={handleChainSelectionChange}
                  />
                  <Alert
                    message="选择的链将用于序列设计，未选择的链保持原始序列不变"
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                </Form.Item>
              )}

              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Form.Item name="num_seq_per_target" label={<span>生成序列数量<Tooltip title="每个目标生成的序列数量"><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip></span>} initialValue={10} style={{ marginBottom: 0 }}>
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="sampling_temp" label={<span>采样温度<Tooltip title="控制序列生成的随机性，值越高越随机"><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip></span>} initialValue={0.1} style={{ marginBottom: 0 }}>
                    <InputNumber min={0.01} max={2.0} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label=" " style={{ marginBottom: 0 }}>
                    <Button type="primary" onClick={prepareSubmitTask} loading={submitting} disabled={!file || selectedChains.length === 0} style={{ width: '100%' }}>
                      提交任务
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {submitting && (
        <div style={{ textAlign: 'center', margin: '24px 0' }}>
          <Spin />
        </div>
      )}

      {error && (
        <Alert message="错误" description={error} type="error" showIcon style={{ marginTop: 16 }} />
      )}

      <Card 
        title="我的任务" 
        style={{ marginTop: 24 }} 
        extra={
          <Space>
            {selectedTaskIds.length > 0 && (
              <Button 
                size="small"
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleBatchDelete}
              >
                批量删除 ({selectedTaskIds.length})
              </Button>
            )}
            <Button size="small" onClick={refreshTasks} icon={<SyncOutlined />}>刷新</Button>
          </Space>
        }
      >
        <Table dataSource={tasks.map(t => ({ ...t, key: t.id }))} columns={columns} pagination={{ pageSize: 10 }} size="small" />
      </Card>

      <Modal 
        title="ProteinMPNN 序列结果查看" 
        open={viewerVisible} 
        onCancel={() => setViewerVisible(false)} 
        width={1200} 
        footer={null}
        destroyOnClose={false}
        forceRender
      >
        {renderSequenceResults()}
      </Modal>

      {/* 参数确认对话框 */}
      <Modal
        title="确认任务参数"
        open={confirmDialogVisible}
        onOk={confirmSubmitTask}
        onCancel={() => {
          setConfirmDialogVisible(false);
          setPendingTaskParams(null);
        }}
        okText="确认提交"
        cancelText="取消"
        width={600}
      >
        {pendingTaskParams && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>请确认以下任务参数：</Text>
            </div>
            
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>选中的链：</Text>
                <span style={{ marginLeft: 8 }}>
                  {pendingTaskParams.selected_chains.map((chain: string) => (
                    <Tag key={chain} color="blue" style={{ marginRight: 4 }}>链 {chain}</Tag>
                  ))}
                </span>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Text strong>生成序列数量：</Text>
                <Text code style={{ marginLeft: 8 }}>{pendingTaskParams.num_seq_per_target}</Text>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Text strong>采样温度：</Text>
                <Text code style={{ marginLeft: 8 }}>{pendingTaskParams.sampling_temp}</Text>
              </div>
              
              <div>
                <Text strong>固定位置：</Text>
                <div style={{ marginTop: 8 }}>
                  {Object.keys(pendingTaskParams.fixed_positions).length > 0 ? (
                    (Object.entries(pendingTaskParams.fixed_positions) as [string, number[]][]).map(([chainId, positions]) => 
                      positions.length > 0 ? (
                        <div key={chainId} style={{ marginBottom: 4 }}>
                          <Text>链 {chainId}：</Text>
                          <Text code style={{ marginLeft: 8 }}>
                            {positions.join(', ')} ({positions.length}个位置)
                          </Text>
                        </div>
                      ) : null
                    )
                  ) : (
                    <Text type="secondary">无固定位置</Text>
                  )}
                </div>
              </div>
            </div>
            
            <Alert
              message="提示"
              description="固定位置的残基将保持原始氨基酸不变，未固定的位置将由ProteinMPNN重新设计。"
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}